// Active-subscriptions section of the Billing page (spec §8). Cancel is the
// one money action that runs in-vault (plain DELETE, no Stripe); everything
// else deep-links out (§6).

import { useEffect, useRef, useState } from 'react';
import { useBilling } from '@/src/hooks/useBilling';
import { useConfig } from '@/src/config/context';
import { useVaultSession } from '@/src/state/session';
import { cancelSubscription, type PaymentsSubscription } from '@/src/marketplace/payments';
import { marketplaceAgentStoreUrl } from '@/lib/marketplace/links';
import { formatCredits } from '@/src/lib/credits';
import { formatUsd } from '@/src/lib/money';
import { periodSuffix } from '@/src/lib/period';

function shortDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

type RowState = { phase: 'idle' | 'confirming' | 'cancelling' | 'error' };

function SubscriptionRow({ sub }: { sub: PaymentsSubscription }) {
  const config = useConfig();
  const { wallet, agentCards } = useVaultSession();
  const billing = useBilling();
  const [row, setRow] = useState<RowState>({ phase: 'idle' });
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Generation guard: bumped whenever the in-flight cancel is superseded
  // (row unmounts, or the user backs out via "Keep") so a stale cancel
  // continuation never overwrites a row that's moved on.
  const generationRef = useRef(0);
  useEffect(() => () => { generationRef.current += 1; }, []);

  useEffect(() => {
    if (row.phase === 'confirming') confirmRef.current?.focus();
  }, [row.phase]);

  // Unlike AgentBlock/VaultSidebar (which shorten a *connected* agent's raw
  // id via displayAgentName as a mid-load fallback), a subscription can
  // outlive the agent's card entirely — showing the full agentId here is
  // more useful than a guessed short name for an agent that's gone.
  const card = agentCards[sub.agentId];
  const name = card?.name ?? sub.agentId;
  const storeUrl = marketplaceAgentStoreUrl(config.marketplaceBaseUrl, sub.agentId);
  const cancelled = sub.status === 'cancelled';

  const doCancel = async () => {
    const generation = ++generationRef.current;
    setRow({ phase: 'cancelling' });
    try {
      await cancelSubscription(config.marketplaceBaseUrl, wallet.signingSigner, sub.agentId);
    } catch {
      if (generationRef.current === generation) setRow({ phase: 'error' });
      return;
    }
    // Cancel succeeded: the row is done regardless of what refetch does.
    // Swallow at the promise level too — a refetch rejection must not become
    // an unhandled rejection (the real refetch is () => void, but test/staging
    // doubles may return a promise).
    Promise.resolve(billing.refetch()).catch(() => {});
  };

  const keep = () => {
    generationRef.current += 1;
    setRow({ phase: 'idle' });
  };

  return (
    <li className="vault-billing__sub-row">
      <span className="vault-billing__sub-name">{name}</span>
      <span>{sub.tierId ?? 'PAYG'} · ${formatUsd(sub.amountUsd)}{periodSuffix(sub.period)}</span>
      <span>{`${cancelled ? 'until' : 'renews'} ${shortDate(sub.renewsAt)}`}</span>
      <span>{formatCredits(sub.creditsAvailableNc)} / {formatCredits(sub.creditsGrantedNc)} cr</span>
      <span className="vault-billing__sub-actions">
        <a href={storeUrl} target="_blank" rel="noreferrer">Manage on marketplace</a>
        {!cancelled && row.phase === 'idle' && (
          <button type="button" onClick={() => setRow({ phase: 'confirming' })}>Cancel</button>
        )}
      </span>
      {(row.phase === 'confirming' || row.phase === 'cancelling' || row.phase === 'error') && !cancelled && (
        <span className="vault-billing__sub-confirm">
          {row.phase === 'error' && <em role="alert">Couldn’t cancel — try again.</em>}
          <span>Credits keep working until {shortDate(sub.renewsAt)}; no refund.</span>
          <button ref={confirmRef} type="button" disabled={row.phase === 'cancelling'} onClick={doCancel}>Confirm cancel</button>
          <button type="button" disabled={row.phase === 'cancelling'} onClick={keep}>Keep</button>
        </span>
      )}
    </li>
  );
}

export function BillingSubscriptions() {
  const billing = useBilling();
  if (billing.status !== 'ready') return null;

  return (
    <section aria-labelledby="billing-subs">
      <h2 id="billing-subs">Active subscriptions</h2>
      {billing.subscriptions.length === 0 ? (
        <p>No active subscriptions.</p>
      ) : (
        <ul className="vault-billing__subs">
          {billing.subscriptions.map((s) => <SubscriptionRow key={s.subscriptionId} sub={s} />)}
        </ul>
      )}
    </section>
  );
}
